import { useMemo, useRef, useState } from 'react';
import { FileCsv, Scroll, UploadSimple } from '@phosphor-icons/react';
import { Button, Select } from 'antd';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { MuteText, ShadowBox } from '../../../elements/Common';
import { AlertFooterBar } from '../../../elements/FooterAlert';
import { CommonUtility, InventoryService, ToastMessage } from '../../../utility';
import { UploadService } from '../../../utility/services/upload';
import { CustomerSearchInput } from '../../../page-components/crm/CustomerSearch';

const FileInput = styled.input`
  opacity: 0;
  position: absolute;
  pointer-events: none;
`;

const FileContainer = styled.div`
  max-width: 100px;
  display: flex;
  align-items: center;
  padding: 10px;
  border: 1px solid ${({ theme }) => theme.colors.grey};
  border-radius: 5px;
  margin-bottom: 10px;
  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .icon {
    min-width: 22px;
  }
`;

const BoxContainer = styled(ShadowBox)`
  width: 50vw;
  min-width: 280px;
`;

export function InventoryImportTab() {
  const [customer, setCustomer] = useState('');
  const [provider, setProvider] = useState('');
  const [file, setFiles] = useState(null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const fileRef = useRef(null);

  const providerOptions = useMemo(
    () =>
      customer?.providers?.map(provider => ({
        value: provider?.wmsMetadata?.wmsWarehouseId,
        label: provider?.warehouseName,
      })) ?? [],
    [customer],
  );

  const selectFiles = e => {
    setFiles(e?.target?.files[0] || null);
  };

  const selectFile = () => {
    fileRef?.current?.click();
  };

  const onCancel = () => {
    reset();
  };

  const onConfirm = async () => {
    try {
      setLoading(true);
      const params = {
        customerOrganizationId: customer.customerOrganizationId,
        warehouseId: provider,
      };
      const res = await InventoryService.generateUploadLInk('inventory-import', params);
      if (res.uploadUrl) {
        await UploadService.media(res.uploadUrl, file);
        ToastMessage.success(
          'File uploaded successfully, check the feed tab for progress',
        );
        reset();
        navigate('/app/inventory/import/feed');
      } else {
        ToastMessage.error('There was some error uploading file.');
      }
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setFiles(null);
    setProvider('');
    setCustomer('');
  };

  return (
    <BoxContainer>
      <MuteText>Search Customers</MuteText>
      <CustomerSearchInput
        placeholder="Search Customer by name"
        onResultClick={setCustomer}
        inputClassName="w-100"
      />
      {customer && (
        <div className="mt-2">
          <MuteText>Search Providers</MuteText>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Search Provider by name"
            options={providerOptions}
            value={provider || undefined}
            onChange={setProvider}
            style={{ width: '100%' }}
          />
        </div>
      )}
      {customer && (
        <div className="mt-4 py-4">
          <div className="d-flex align-items-center">
            <Scroll size={18} />
            <b className="ml-1">Import inventory for {customer?.organizationName}</b>
          </div>
          <div className="mt-4 d-flex flex-column">
            <p className="m-0">You can dowload a template from here</p>
            <Button
              href={CommonUtility.getCdnLink('wms/Templates/inventory.csv')}
              download
              target="_blank"
              rel="noreferrer"
            >
              Download Template
            </Button>
          </div>
          <div className="d-flex flex-column mt-4">
            <MuteText>Import from file</MuteText>
            {file && (
              <FileContainer>
                <FileCsv size={22} className="icon" />
                <span>{file.name}</span>
              </FileContainer>
            )}
            <FileInput accept=".csv" onChange={selectFiles} ref={fileRef} type="file" />
            <Button
              onClick={selectFile}
              icon={<UploadSimple size={18} />}
              style={{ alignSelf: 'flex-start' }}
            >
              Select File
            </Button>
          </div>
        </div>
      )}
      {file && (
        <AlertFooterBar
          message="Upload File"
          saveButtonText="Upload"
          loading={loading}
          save={onConfirm}
          cancel={onCancel}
        />
      )}
    </BoxContainer>
  );
}
